import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "@/components/ui/input";
import ImageUploadPreview from "../image-upload-preview";
import BoldLayout from "../bold-layout";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";
import type {
	ApiPage,
	ApiPageField,
	Brandkit,
	FormBrand,
} from "../../types/api.types";
import {
	buildFieldSchema,
	type DynamicFormData,
} from "../../utils/field-helpers";

interface StepAboutCompanyProps {
	brandkit: Brandkit;
	brand: FormBrand;
	showBackButton?: boolean;
	onBack?: () => void;
	stepKey?: string;
	pageConfig: ApiPage;
	totalSteps: number;
	currentStepIndex: number;
	isDarkMode?: boolean;
}

export default function StepAboutCompany({
	brandkit,
	brand,
	showBackButton,
	onBack,
	stepKey,
	pageConfig,
	totalSteps,
	currentStepIndex,
	isDarkMode,
}: StepAboutCompanyProps) {
	const {
		data,
		setCompanyInfo,
		nextStep,
		updateCompanyInfoStep,
		isSubmitting,
		submitError,
	} = useFeedbackFlow();
	const [error, setError] = useState<string | null>(null);

	const visibleFields = useMemo(
		() =>
			(pageConfig.fields ?? [])
				.filter((f) => f.is_visible)
				.sort((a, b) => a.sort_order - b.sort_order),
		[pageConfig.fields],
	);

	const schema = useMemo(() => {
		const shape: Record<string, z.ZodTypeAny> = {};
		for (const field of visibleFields) {
			if (field.field_type !== "file") {
				shape[field.field_key] = buildFieldSchema(field);
			}
		}
		return z.object(shape);
	}, [visibleFields]);

	const defaultValues = useMemo(() => {
		const values: Record<string, string> = {};
		const companyInfo = data.companyInfo as unknown as Record<string, unknown>;
		for (const field of visibleFields) {
			if (field.field_type !== "file") {
				values[field.field_key] =
					(companyInfo[field.field_key] as string) ?? "";
			}
		}
		return values;
	}, [visibleFields, data.companyInfo]);

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
	} = useForm<DynamicFormData>({
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		resolver: zodResolver(schema) as any,
		mode: "onChange",
		defaultValues,
	});

	const onSubmit = async (formData: DynamicFormData) => {
		const companyInfoUpdate: Record<string, string> = {};
		for (const field of visibleFields) {
			if (field.field_type !== "file") {
				companyInfoUpdate[field.field_key] =
					formData[field.field_key] || "";
			}
		}
		setCompanyInfo(companyInfoUpdate);

		try {
			setError(null);
			await updateCompanyInfoStep();
			nextStep();
		} catch (err) {
			setError(
				submitError ||
					"Failed to save company information. Please try again.",
			);
		}
	};

	const handleLogoChange = (file: File | null, preview: string | null) => {
		setCompanyInfo({
			logoFile: file,
			logoPreview: preview,
		});
	};

	const brandLogo =
		brandkit?.logo_icon_with_label_url ||
		brandkit?.logo_icon_only_url ||
		brandkit?.logo_label_only_url ||
		brandkit?.logo_inverted_icon_with_label_url ||
		brandkit?.logo_inverted_icon_only_url ||
		brandkit?.logo_inverted_label_only_url ||
		null;

	const renderField = (field: ApiPageField) => {
		if (field.field_type === "file") {
			return (
				<ImageUploadPreview
					key={field.field_key}
					label={field.label}
					preview={data.companyInfo.logoPreview}
					onChange={handleLogoChange}
					icon="building"
					isDark={isDarkMode}
					freeAspect
				/>
			);
		}

		const inputType =
			field.field_type === "url"
				? "url"
				: field.field_type === "email"
					? "email"
					: field.field_type === "tel"
						? "tel"
						: "text";

		return (
			<Input
				key={field.field_key}
				label={field.label}
				type={inputType}
				placeholder={field.placeholder ?? undefined}
				required={field.is_required}
				error={errors[field.field_key]?.message as string | undefined}
				{...register(field.field_key)}
				isDark={isDarkMode}
			/>
		);
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="contents">
			<BoldLayout
				brandkit={brandkit}
				showBranding={brand?.branding !== "disabled"}
				brandName={brand.name}
				brandLogo={brandLogo}
				stepKey={stepKey}
				showBackButton={showBackButton}
				onBack={onBack}
				title={pageConfig.attributes.title}
				description={pageConfig.attributes.description}
				progress={{ current: currentStepIndex + 1, total: totalSteps }}
				isFormSubmit
				continueDisabled={!isValid || isSubmitting}
				continueLabel={isSubmitting ? "Saving..." : "Continue"}
				centered={false}
				isDarkMode={isDarkMode}
			>
				<div className="space-y-8">
					{visibleFields.map(renderField)}
				</div>

				{error && (
					<div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
						<p className="text-sm text-red-600">{error}</p>
						<button
							type="submit"
							className="mt-2 text-sm text-red-700 underline hover:no-underline"
						>
							Try Again
						</button>
					</div>
				)}
			</BoldLayout>
		</form>
	);
}
